import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowLeft, ArrowRight, ChevronDown } from "lucide-react";
import { useState } from "react";
import Seo from "@/lib/Seo";
import PageHero from "@/components/common/PageHero";
import { easeOut } from "@/components/common/Reveal";
import { getPost, getRelatedPosts, BLOG_POSTS } from "@/data/blog";
import { useLead } from "@/components/common/LeadModal";
import { Button } from "@/components/ui/button";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });

function Block({ block }) {
  if (block.type === "p") return <p className="my-4 leading-relaxed text-slate-600">{block.text}</p>;
  if (block.type === "h2") return <h2 className="mt-10 font-display text-2xl font-bold text-brand-blue">{block.text}</h2>;
  if (block.type === "h3") return <h3 className="mt-6 font-display text-lg font-bold text-brand-blue">{block.text}</h3>;
  if (block.type === "ul") return (
    <ul className="my-4 space-y-2">
      {block.items.map((it, i) => (
        <li key={i} className="flex items-start gap-2 text-slate-600">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-red" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
  if (block.type === "faq") return <Faq items={block.items} />;
  return null;
}

function Faq({ items }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="my-8 space-y-3">
      <h2 className="font-display text-2xl font-bold text-brand-blue">Questions fréquentes</h2>
      {items.map((it, i) => (
        <div key={i} className="overflow-hidden rounded-xl border border-slate-200">
          <button onClick={() => setOpen(open === i ? null : i)}
            className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left font-semibold text-brand-blue transition-colors hover:bg-slate-50">
            <span>{it.q}</span>
            <ChevronDown className={`h-5 w-5 shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
          </button>
          {open === i && (
            <div className="border-t border-slate-100 px-5 py-4 text-sm text-slate-600">{it.a}</div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function BlogArticle() {
  const { slug } = useParams();
  const post = getPost(slug);
  const { openLead } = useLead();

  if (!post) return <Navigate to="/blog" replace />;

  const related = getRelatedPosts(slug, 3);
  const faq = post.content.find((b) => b.type === "faq");

  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.metaDescription,
    image: post.cover,
    datePublished: post.publishedAt,
    dateModified: post.publishedAt,
    author: { "@type": "Organization", name: "EDUCASER GROUP" },
    publisher: {
      "@type": "Organization",
      name: "EDUCASER GROUP",
      logo: { "@type": "ImageObject", url: "https://educaser.fr/qualiopi.png" },
    },
    ...(faq && {
      mainEntity: faq.items.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    }),
  };

  return (
    <>
      <Seo title={post.metaTitle || post.title} description={post.metaDescription || post.excerpt}
        path={`/blog/${post.slug}`} image={post.cover} schema={schema} />

      <PageHero
        breadcrumb={[{ label: "Accueil", to: "/" }, { label: "Blog", to: "/blog" }, { label: post.title }]}
        eyebrow={post.category}
        title={post.title}
      />

      <article className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {fmtDate(post.publishedAt)}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {post.readingTime} min de lecture</span>
            <span>— {post.author}</span>
          </div>

          <motion.img
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: easeOut }}
            src={post.cover} alt={post.title}
            className="mt-8 aspect-[16/9] w-full rounded-3xl object-cover"
          />

          <div className="prose-content mt-10">
            {post.content.map((b, i) => <Block key={i} block={b} />)}
          </div>

          {/* CTA in-article */}
          <div className="mt-14 rounded-3xl bg-brand-blue p-8 text-white sm:p-10" data-testid="blog-cta">
            <h3 className="font-display text-2xl font-bold">Envie de vous former ?</h3>
            <p className="mt-2 text-white/80">Nos conseillers vous accompagnent gratuitement dans votre projet et votre financement.</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button onClick={() => openLead("devis")} className="rounded-full bg-brand-red hover:bg-brand-red-light">
                Demander un devis
              </Button>
              <Button onClick={() => openLead("rappel")} variant="outline" className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10">
                Être rappelé
              </Button>
            </div>
          </div>

          <div className="mt-10">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-red transition-colors hover:text-brand-red-light">
              <ArrowLeft className="h-4 w-4" /> Retour aux articles
            </Link>
          </div>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <div className="border-t border-slate-100 bg-slate-50 py-16">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <h2 className="font-display text-2xl font-bold text-brand-blue">À lire aussi</h2>
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`}
                  className="group block overflow-hidden rounded-2xl bg-white shadow-[0_10px_30px_rgba(0,35,102,0.06)] transition-all hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(0,35,102,0.12)]">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={r.cover} alt={r.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  </div>
                  <div className="p-5">
                    <p className="text-xs text-slate-400">{r.category}</p>
                    <h3 className="mt-1 font-display text-lg font-bold text-brand-blue group-hover:text-brand-red">{r.title}</h3>
                    <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-red">
                      Lire <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
